import { post, request } from '../api';

export type SourceKind = 'stripe' | 'paypal' | 'woocommerce';
export type FinancialKind = 'payment' | 'refund' | 'fee';

export interface SyncStateView {
  lastSuccessAt: string | null;
  failureCount: number;
  nextRetryAt: string | null;
  lastError: string | null;
  updatedAt: string;
}

export interface ExternalResource {
  id: string;
  businessId: string;
  businessName: string;
  kind: SourceKind | null;
  name: string;
  credential: { id: string; name: string; provider: string; revoked: boolean } | null;
  config: Record<string, unknown>;
  enabled: boolean;
  createdAt: string;
  observations: number;
  state: SyncStateView | null;
}

export interface SummaryRow {
  businessId: string;
  month: string;
  currency: string;
  revenue: number;
  refunds: number;
  fees: number;
  net: number;
  aiCost: number;
}

export interface FinanceSummary {
  from: string;
  to: string;
  businesses: Array<{ id: string; name: string; currency: string; timezone: string }>;
  rows: SummaryRow[];
}

export interface FinancialObservationRow {
  id: string;
  businessId: string;
  businessName: string;
  resourceId: string | null;
  source: SourceKind;
  kind: FinancialKind;
  externalId: string;
  amount: string;
  currency: string;
  occurredAt: string;
  observedAt: string;
}

const qs = (params: Record<string, string | number | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const externalApi = {
  resources: (businessId?: string) => request<ExternalResource[]>(`/external/resources${qs({ businessId })}`),
  createResource: (input: { businessId: string; kind: SourceKind; name: string; credentialId: string; config: Record<string, unknown> }) => post<ExternalResource>('/external/resources', input),
  updateResource: (id: string, patch: { name?: string; credentialId?: string; config?: Record<string, unknown>; enabled?: boolean }) => request<ExternalResource>(`/external/resources/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  syncResource: (id: string) => post<{ jobId: string; state: string; deduplicated: boolean }>(`/external/resources/${id}/sync`, {}),
  resourceState: (id: string) => request<SyncStateView & { resourceId: string; cursor: string | null }>(`/external/resources/${id}/state`),

  summary: (from: string, to: string, businessId?: string) => request<FinanceSummary>(`/finance/summary${qs({ from, to, businessId })}`),
  observations: (params: { businessId?: string; from?: string; to?: string; kind?: FinancialKind; source?: SourceKind; page?: number; pageSize?: number }) =>
    request<{ rows: FinancialObservationRow[]; total: number; page: number; pageSize: number }>(`/finance/observations${qs(params)}`),
};
